import { APIStatus } from "@/api/APIStatus.type";
import {
  PlayStylesLabResponse,
  calculatePlayStylesLab,
} from "@/api/privateRequests/players/playstyles-lab/calculatePlayStylesLab";
import { mixpanelTrack } from "@/events/mixpanelTrack";
import { DetailedPlayerType } from "@/pages/DetailedPlayer/DetailedPlayer";
import {
  PayloadAction,
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { deriveStepUpgrades, groupUpgrades } from "@repo/shared/evolutions";
import type { UpgradeType } from "@repo/shared/types";

import { convertToEvolutionBuilderPlayer } from "../evolutions/convertToEvolutionBuilderPlayer";
import { EvolutionBuilderPlayer } from "../evolutions/evolutionsBuilderSlice";
import { MetaRatingsPlayer } from "../players/playersSlice";
import { IRootState } from "../store";

export const MAX_PLAYSTYLES_PLUS = 4;
export const MAX_PLAYSTYLES = 8;

export type PlayStylesLabMetaRating = {
  value: number;
  delta: number;
};

export type PlayStylesLabRecommendation = {
  metaRating: PlayStylesLabMetaRating;
  playerRoleId: number;
  playStyles: number[];
  playStylesPlus: number[];
};

type PlayStylesLabState = {
  basePlayer: EvolutionBuilderPlayer | null;
  basePlayerWithPlaystyles: EvolutionBuilderPlayer | null;
  labPlayStyles: number[];
  labPlayStylesPlus: number[];
  recommendations: PlayStylesLabRecommendation[];
  // Every PlayStyle (and PlayStyle+), rendered as-is from the latest response.
  // The backend always returns the full set and refreshes meta rating + delta.
  allPlayStyles: PlayStylesLabRecommendation[];
  // Diff between the base player and the player with the applied PlayStyles
  // (added PlayStyles + meta rating gain), driving the top section.
  totalUpgrades: UpgradeType[];
  // The role the player is currently displayed at. undefined => "Best".
  activePlayerRoleId?: number;
  aboutToChangePlayerRoleId?: number;
  // True once the system (e.g. applying a recommendation) changed the role.
  systemChanged: boolean;
  status: APIStatus;
};

const initialState: PlayStylesLabState = {
  basePlayer: null,
  basePlayerWithPlaystyles: null,
  labPlayStyles: [],
  labPlayStylesPlus: [],
  recommendations: [],
  allPlayStyles: [],
  totalUpgrades: [],
  activePlayerRoleId: undefined,
  aboutToChangePlayerRoleId: undefined,
  systemChanged: false,
  status: "idle",
};

interface FetchPlayStylesLabParams {
  evoPathId?: string;
  evolutionPath?: number[];
  pathwayPath?: number[][];
}

export const fetchPlayStylesLab = createAsyncThunk<
  { response: PlayStylesLabResponse; aboutToChangePlayerRoleId: number },
  FetchPlayStylesLabParams,
  { state: IRootState }
>("playStylesLab/fetch", async (params, { getState }) => {
  const {
    basePlayer,
    labPlayStyles,
    labPlayStylesPlus,
    activePlayerRoleId,
    aboutToChangePlayerRoleId,
  } = getState().playStylesLabReducer;

  const playerRoleId = aboutToChangePlayerRoleId || activePlayerRoleId;

  const res = await calculatePlayStylesLab({
    resourceId: basePlayer.resourceId,
    assetId: basePlayer.assetId,
    labPlayStylesPlus,
    labPlayStyles,
    ...(playerRoleId ? { playerRoleId } : {}),
    ...(params.evoPathId ? { evoPathId: params.evoPathId } : {}),
    ...(params.evolutionPath
      ? { evolutionPath: params.evolutionPath, pathwayPath: params.pathwayPath }
      : {}),
  });
  return { response: res, aboutToChangePlayerRoleId };
});

const playStylesLabSlice = createSlice({
  name: "playStylesLab",
  initialState,
  reducers: {
    addPlayerToPlayStylesLab: (
      state,
      action: PayloadAction<{
        player: MetaRatingsPlayer | DetailedPlayerType;
        labPlayStyles?: number[];
        labPlayStylesPlus?: number[];
        // Keeps the role the player arrived with (e.g. their squad slot's
        // role) instead of falling back to "Best".
        playerRoleId?: number;
      }>,
    ) => {
      const convertedPlayer = convertToEvolutionBuilderPlayer(
        action.payload.player,
      );
      state.basePlayer = convertedPlayer;
      state.basePlayerWithPlaystyles = convertedPlayer;
      state.labPlayStyles = action.payload.labPlayStyles ?? [];
      state.labPlayStylesPlus = action.payload.labPlayStylesPlus ?? [];
      state.allPlayStyles = [];
      state.activePlayerRoleId = action.payload.playerRoleId;
      state.systemChanged = false;
      mixpanelTrack("added_player_to_playstyles_lab", {
        playerId: action.payload.player.resourceId,
        playerName: action.payload.player.name,
      });
    },
    applyPlayStyle: (
      state,
      action: PayloadAction<{ id: number; plus: boolean }>,
    ) => {
      const { id, plus } = action.payload;
      const key = plus ? "labPlayStylesPlus" : "labPlayStyles";
      const oppositeKey = plus ? "labPlayStyles" : "labPlayStylesPlus";
      const isRemoving = state[key].includes(id);
      state[key] = isRemoving
        ? state[key].filter((x) => x !== id)
        : [...state[key], id];
      if (isRemoving) {
        // Optimistic: drop the upgrade chip now, before the refetch lands.
        state.totalUpgrades = state.totalUpgrades.filter(
          (u) =>
            !(
              u.type === "play-styles-upgrade" &&
              u.playStyleId === id &&
              u.isPlus === plus
            ),
        );
      } else if (state[oppositeKey].includes(id)) {
        // Same id already applied in the other list — swap it over.
        state[oppositeKey] = state[oppositeKey].filter((x) => x !== id);
        state.totalUpgrades = state.totalUpgrades.filter(
          (u) =>
            !(
              u.type === "play-styles-upgrade" &&
              u.playStyleId === id &&
              u.isPlus === !plus
            ),
        );
      }
    },
    applyRecommendation: (
      state,
      action: PayloadAction<PlayStylesLabRecommendation>,
    ) => {
      state.labPlayStyles = [
        ...state.labPlayStyles,
        ...action.payload.playStyles,
      ];
      state.labPlayStylesPlus = [
        ...state.labPlayStylesPlus,
        ...action.payload.playStylesPlus,
      ];
      state.aboutToChangePlayerRoleId = action.payload.playerRoleId;
      state.systemChanged = true;
    },
    setActivePlayerRole: (state, action: PayloadAction<number | undefined>) => {
      state.activePlayerRoleId = action.payload;
    },
    resetPlayStyles: (state) => {
      state.labPlayStyles = [];
      state.labPlayStylesPlus = [];
      // Optimistic: clear the upgrade chips now, before the refetch lands.
      state.totalUpgrades = state.totalUpgrades.filter(
        (u) => u.type !== "play-styles-upgrade",
      );
      if (state.systemChanged) {
        // Revert to "Best"; the player role updates from the next response.
        state.activePlayerRoleId = undefined;
        state.systemChanged = false;
      }
    },
    clearPlayStylesLab: (state) => {
      state.basePlayer = null;
      state.basePlayerWithPlaystyles = null;
      state.labPlayStyles = [];
      state.labPlayStylesPlus = [];
      state.recommendations = [];
      state.allPlayStyles = [];
      state.totalUpgrades = [];
      state.activePlayerRoleId = undefined;
      state.systemChanged = false;
      state.status = "idle";
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPlayStylesLab.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchPlayStylesLab.fulfilled, (state, action) => {
      const { response, aboutToChangePlayerRoleId } = action.payload;
      const {
        baseStep,
        detailedPlayer,
        currentStep,
        recommendations,
        allPlayStyles,
      } = response;

      if (!state.basePlayer && baseStep) {
        state.basePlayer = convertToEvolutionBuilderPlayer(baseStep);
      }
      if (detailedPlayer) {
        state.basePlayerWithPlaystyles =
          convertToEvolutionBuilderPlayer(detailedPlayer);
        // Preserve the actively selected role across refetches.
        if (state.activePlayerRoleId !== undefined) {
          state.basePlayerWithPlaystyles.playerRoleId =
            state.activePlayerRoleId;
        }
      }
      const isGk = detailedPlayer?.preferredPosition === "GK";
      const upgrades = currentStep ? deriveStepUpgrades(currentStep, isGk) : [];
      // The backend's diff reports the player's innate PlayStyles too; only the
      // ones the user added in the lab belong after the "+" in the top section.
      state.totalUpgrades = upgrades.filter(
        (u) =>
          u.type !== "play-styles-upgrade" ||
          (u.isPlus ? state.labPlayStylesPlus : state.labPlayStyles).includes(
            u.playStyleId,
          ),
      );
      state.recommendations = recommendations ?? [];
      if (aboutToChangePlayerRoleId) {
        state.activePlayerRoleId = aboutToChangePlayerRoleId;
        state.aboutToChangePlayerRoleId = undefined;
      }
      state.allPlayStyles = allPlayStyles ?? [];
      state.status = "fulfilled";
    });
    builder.addCase(fetchPlayStylesLab.rejected, (state) => {
      state.status = "rejected";
    });
  },
});

export const {
  addPlayerToPlayStylesLab,
  applyPlayStyle,
  applyRecommendation,
  setActivePlayerRole,
  resetPlayStyles,
  clearPlayStylesLab,
} = playStylesLabSlice.actions;

export const playStylesLabSelector = (state: IRootState) =>
  state.playStylesLabReducer;

export const getCurrentPlayStylesLabPlayerSelector = (state: IRootState) =>
  state.playStylesLabReducer.basePlayerWithPlaystyles ||
  state.playStylesLabReducer.basePlayer;

export const playStylesLabRecommendationsSelector = createSelector(
  [
    (state: IRootState) => state.playStylesLabReducer.recommendations,
    (state: IRootState) => state.playStylesLabReducer.status,
  ],
  (recommendations, status) => ({ recommendations, status }),
);

export const playStylesLabStepsSelector = createSelector(
  [
    (state: IRootState) => state.playStylesLabReducer.allPlayStyles,
    (state: IRootState) => state.playStylesLabReducer.status,
    (state: IRootState) => state.playStylesLabReducer.systemChanged,
    (state: IRootState) => state.playStylesLabReducer.aboutToChangePlayerRoleId,
  ],
  (allPlayStyles, status, systemChanged, aboutToChangePlayerRoleId) => ({
    playStylesPlus: allPlayStyles.filter((p) => p.playStylesPlus.length > 0),
    playStyles: allPlayStyles.filter((p) => p.playStyles.length > 0),
    status,
    systemChanged,
    aboutToChangePlayerRoleId,
  }),
);

export const playStylesLabAppliedSelector = createSelector(
  [
    (state: IRootState) => state.playStylesLabReducer.labPlayStyles,
    (state: IRootState) => state.playStylesLabReducer.labPlayStylesPlus,
    (state: IRootState) => state.playStylesLabReducer.activePlayerRoleId,
    (state: IRootState) => state.playStylesLabReducer.systemChanged,
  ],
  (labPlayStyles, labPlayStylesPlus, activePlayerRoleId, systemChanged) => ({
    labPlayStyles,
    labPlayStylesPlus,
    activePlayerRoleId,
    systemChanged,
  }),
);

export const playStylesLabUpgradesSelector = createSelector(
  [(state: IRootState) => state.playStylesLabReducer.totalUpgrades],
  groupUpgrades,
);

export default playStylesLabSlice.reducer;
